import { lazy } from 'react';

import { ReturnT } from '~/components/payment-beta/types';
import { PaymentMethodT } from '~/components/payment-beta/types/deposit';
import { AccountT } from '~/components/payment-beta/types/templates';
import { Templates, FormTemplateCode as TemplateCode } from '~/components/payment-beta/utils/constants';
import validate from '~/components/payment-beta/validations/templates';

type SpecT = {
	[key: string]: unknown;
} | null;

// FIXME
type AccountValidatorT = (data: AccountT & Record<string, unknown>, method: PaymentMethodT) => SpecT | Promise<SpecT>;

export const templateCodeMap: Record<string, Templates> = {
	// Credit card
	[TemplateCode.CREDIT_CARD]: Templates.CREDIT_CARD,
	[TemplateCode.DIRECTLY_ROUTED_CC]: Templates.CREDIT_CARD,
	[TemplateCode.ASTROPAY_CC]: Templates.CREDIT_CARD,
	[TemplateCode.CRED_CARD]: Templates.CREDIT_CARD,

	// Paymero
	[TemplateCode.PAYMERO_NET_BANKING]: Templates.PAYMERO,
	[TemplateCode.AKONTO_PAY_NET_BANKING]: Templates.PAYMERO,
	[TemplateCode.ZOTAPAY_NET_BANKING]: Templates.PAYMERO,
	[TemplateCode.SAFEXPAY_NETBANKING]: Templates.PAYMERO,
	[TemplateCode.PAYMERO_WALLET]: Templates.PAYMERO,

	// Pay Blick
	[TemplateCode.DOTPAY_BLIK]: Templates.PAY_BLIK,
	[TemplateCode.TPAY_BLIK]: Templates.PAY_BLIK,

	// TSI_VOUCHER
	[TemplateCode.TSI]: Templates.TSI_VOUCHER,
	[TemplateCode.VOUCHER]: Templates.TSI_VOUCHER,
	// Others
	[TemplateCode.SWISH]: Templates.SWISH,
	[TemplateCode.EMAIL]: Templates.EMAIL,
	[TemplateCode.EMAIL_CURRENCIES]: Templates.EMAIL_CURRENCIES,
	[TemplateCode.ONLY_CURRENCIES]: Templates.ONLY_CURRENCIES,
	[TemplateCode.EMAIL_MONEYBOOKERS]: Templates.EMAIL_MONEYBOOKERS,
	[TemplateCode.PHONE]: Templates.PHONE,
	[TemplateCode.VIRTUAL_CREDIT_CARD]: Templates.VIRTUAL_CREDIT_CARD,
	[TemplateCode.PEER_TO_PEER]: Templates.PEER_TO_PEER,
	[TemplateCode.EMP_CASHLIB]: Templates.EMP_CASHLIB,
	[TemplateCode.IMOJE_BANK]: Templates.IMOJE_BANK,
	[TemplateCode.NETELLER]: Templates.NETELLER,
	[TemplateCode.ECOBANQ]: Templates.ECOBANQ,
	[TemplateCode.EZEE_WALLET]: Templates.EZEE_WALLET,
	[TemplateCode.PAYMERO_UPI]: Templates.PAYMERO_UPI,
	[TemplateCode.AKONTO_PAY_UPI]: Templates.AKONTO_PAY_UPI,
	[TemplateCode.MUCHBETTER]: Templates.MUCHBETTER,
	[TemplateCode.VENUSPOINT_DEPOSIT]: Templates.VENUSPOINT_DEPOSIT
};

export const TemplateComponent: Record<Templates, ReturnT> = {
	[Templates.NO_FIELDS]: () => null,
	[Templates.FOGHORN_DEPOSIT]: () => null,
	[Templates.SWISH]: lazy(() => import('./swish-template')),
	[Templates.EMAIL]: lazy(() => import('./email-template')),
	[Templates.EMAIL_CURRENCIES]: lazy(() => import('./email-currencies')),
	[Templates.EMAIL_MONEYBOOKERS]: lazy(() => import('./skrill-template')),
	[Templates.ONLY_CURRENCIES]: lazy(() => import('./only-currencies')),
	[Templates.CREDIT_CARD]: lazy(() => import('./credit-card')),
	[Templates.VIRTUAL_CREDIT_CARD]: lazy(() => import('./virtual-credit-card')),
	[Templates.IMOJE_BANK]: lazy(() => import('./imoje-template')),
	[Templates.NETELLER]: lazy(() => import('./netteller')),
	[Templates.PEER_TO_PEER]: () => null,
	[Templates.EMP_CASHLIB]: lazy(() => import('./cash-lib')),
	[Templates.ECOBANQ]: lazy(() => import('./ecobanq')),
	[Templates.EZEE_WALLET]: lazy(() => import('./ezeewallet')),
	[Templates.PAYMERO_UPI]: lazy(() => import('./paymero-upi')),
	[Templates.AKONTO_PAY_UPI]: lazy(() => import('./akontopay-upi')),
	[Templates.MUCHBETTER]: lazy(() => import('./muchbetter')),
	[Templates.PAYMERO]: () => null,
	[Templates.PHONE]: lazy(() => import('./adyen-mobilepay')),
	[Templates.PAY_BLIK]: () => null,
	[Templates.TSI_VOUCHER]: () => null,
	[Templates.VENUSPOINT_DEPOSIT]: () => null
};

const AccountValidator: Record<string, AccountValidatorT> = {
	[Templates.CREDIT_CARD]: (account, method) => validate.creditcard(account, method),
	[Templates.NETELLER]: account => validate.netteller(account),
	[Templates.EMAIL]: account => validate.email(account),
	[Templates.EMAIL_CURRENCIES]: account => validate.emailCurrency(account),
	[Templates.ONLY_CURRENCIES]: account => validate.onlyCurrency(account),
	[Templates.ECOBANQ]: account => validate.ecoBanq(account),
	[Templates.EZEE_WALLET]: account => validate.ezeeWallet(account),
	[Templates.PAYMERO_UPI]: account => validate.paymero(account),
	[Templates.PHONE]: account => validate.phone(account),
	[Templates.AKONTO_PAY_UPI]: account => validate.akontoPay(account)
	// [Templates.PAYMERO]: (method, account) => validate.paymero(method, account)
	// [Templates.PAY_BLIK]: (method, account) => validate.blikPay(method, account)
};

export const getTemplate = (templateCode?: string): [ReturnT | null, AccountValidatorT | null, Templates | null] => {
	if (!templateCode) {
		return [null, null, null];
	}

	const templateType = templateCodeMap?.[templateCode ?? Templates.NO_FIELDS] ?? Templates.NO_FIELDS;

	return [
		TemplateComponent[templateType],
		AccountValidator[templateType] ?? null,
		templateCode ? templateType : null
	];
};
